import { useNotifications as useToolpadNotifications } from '@toolpad/core/useNotifications';

/**
 * Toolpad renders plain SnackbarContent (neutral / “black bar”) when `severity` is omitted, and leaves
 * `autoHideDuration` unset unless we provide it — so we default severity + duration for all `show` calls.
 */
const AUTO_HIDE_MS: Record<'success' | 'error' | 'info' | 'warning', number> = {
  success: 2000,
  error: 2000,
  info: 2000,
  warning: 2000,
};

type NotificationOptions = Parameters<ReturnType<typeof useToolpadNotifications>['show']>[1];

type NotifyOptions = Omit<NotificationOptions, 'severity'>;

const withDefaults = (options?: NotificationOptions): NotificationOptions => {
  const o = options ?? {};
  const severity = o.severity ?? 'info';
  const next: NotificationOptions = { ...o, severity };
  if (next.autoHideDuration === undefined) {
    next.autoHideDuration = AUTO_HIDE_MS[severity];
  }
  return next;
};

export const useNotifications = () => {
  const notifications = useToolpadNotifications();

  const show = (message: string, options?: NotificationOptions) => {
    return notifications.show(message, withDefaults(options));
  };

  const notifySuccess = (message: string, options?: NotifyOptions) => {
    return show(message, { ...options, severity: 'success' });
  };

  const notifyError = (message: string, options?: NotifyOptions) => {
    return show(message, { ...options, severity: 'error' });
  };

  const notifyWarning = (message: string, options?: NotifyOptions) => {
    return show(message, { ...options, severity: 'warning' });
  };

  return {
    ...notifications,
    show,
    notifySuccess,
    notifyError,
    notifyWarning,
  };
};

export default useNotifications;
